// Is this event date claimable against this policy?
//
// A claim logged with the event date on the day the policy commenced is not payable:
// the engine rejects it with "Claim event date <date> is outside policy coverage
// period". It did so only AFTER the claim had been created, though, so the file
// existed, sat in the queue and had to be chased down — CL-OMK0000224 on staging is
// exactly that, event date 2026-02-23 against a policy that commenced 2026-02-23.
// Deciding it before the claim is logged is the whole point of this module.
//
// Kept in lib/ because the customer journey applies the same rule; the two must not
// drift.
//
// Dates are compared as plain ISO strings. That is not laziness: yyyy-mm-dd sorts
// lexicographically in date order, whereas the validator rule this replaced
// (`maxNumber:<today>`) ran parseFloat over the value, so "2026-08-07" became 2026
// and it only ever compared years — a future date inside the current year passed.
//
// No optional chaining / ES6+ syntax the project's Babel config rejects.

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Today as yyyy-mm-dd in the user's own timezone, not UTC. */
export function todayIso(now) {
  const ref = now instanceof Date ? now : new Date();
  const month = String(ref.getMonth() + 1);
  const day = String(ref.getDate());
  return (
    ref.getFullYear() +
    '-' +
    (month.length === 1 ? '0' + month : month) +
    '-' +
    (day.length === 1 ? '0' + day : day)
  );
}

function isIso(value) {
  return typeof value === 'string' && ISO_DATE.test(value);
}

/**
 * Why this event date cannot be claimed, or null when it is fine.
 *
 * Returns the message to show rather than a boolean, so the caller does not have to
 * work out which of the two rules it tripped.
 *
 * Nothing is assumed when data is absent: an unparseable or missing policy start
 * date means the commencement rule simply does not run, so a claim is never blocked
 * over a date the caller never had.
 *
 * @param {object} input
 *   eventDate              yyyy-mm-dd, as typed
 *   policyStartDate        yyyy-mm-dd, the selected policy's commencement date
 *   blockCommencementDay   whether the commencement-day rule applies (OMK)
 *   today                  optional Date, for tests
 * @returns {string|null}
 */
export function eventDateProblem(input) {
  const opts = input || {};
  const eventDate = opts.eventDate;
  if (!isIso(eventDate)) {
    // Empty or half-typed: `required` on the field already covers this, and
    // complaining mid-keystroke would just be noise.
    return null;
  }

  if (eventDate > todayIso(opts.today)) {
    return 'The event date cannot be in the future.';
  }

  if (opts.blockCommencementDay && isIso(opts.policyStartDate)) {
    if (eventDate === opts.policyStartDate) {
      return (
        'Cover started on ' +
        opts.policyStartDate +
        '. A claim cannot be logged for the day the policy commenced.'
      );
    }
    if (eventDate < opts.policyStartDate) {
      return (
        'Cover started on ' +
        opts.policyStartDate +
        ', so this event falls outside the policy coverage period.'
      );
    }
  }

  return null;
}
